import { useAppContext } from "../../../2_ksbHub/context/appContext";
import { useEventManagerContext } from "../context/eventContext";
import eventService from "../services/eventService";

export default function useEventActions(options = {}) {
  const { showLockScreen } = options;
  const {
    isAnEventManagerAdmin,
    loadSommerData,
    selectedSchoolyear,
    selectedEvent,
    setSelectedEvent,
    setAngebote,
    shifts,
    setShifts,
  } = useEventManagerContext();

  const { currentUser } = useAppContext();

  /**
   * =========================================================
   * ✅ RESERVE SLOT (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleReserve = async (angebotId, angebot) => {
    if (showLockScreen || !currentUser) return;

    const isMine = angebot?.reservedBy?.id === currentUser.id;
    const isReserved = !!angebot?.reservedBy;

    let userIdToSend;

    if (isMine) {
      userIdToSend = null;
    } else if (isAnEventManagerAdmin && isReserved) {
      userIdToSend = null;
    } else {
      userIdToSend = currentUser.id;
    }

    // ✅ optimistic update
    setAngebote((prev) =>
      prev.map((a) =>
        a.id !== angebotId
          ? a
          : {
              ...a,
              reservedBy: userIdToSend === null ? null : { id: userIdToSend },
            },
      ),
    );

    try {
      const res = await eventService.reserveSlot(
        angebotId,
        userIdToSend,
        isAnEventManagerAdmin,
      );

      // ✅ ✅ IMPORTANT
      if (!res.success) {
        // console.log("Res not successful!");
        throw new Error("Slot already taken");
      }

      // ✅ sync exact backend result (optional but nice)
      if (res.reservedBy !== undefined) {
        setAngebote((prev) =>
          prev.map((a) =>
            a.id === angebotId ? { ...a, reservedBy: res.reservedBy } : a,
          ),
        );
      }
    } catch (err) {
      console.warn("Race condition → rollback", err);

      // ❌ rollback to correct state
      setAngebote((prev) =>
        prev.map((a) => (a.id === angebotId ? angebot : a)),
      );
    }
  };

  /**
   * =========================================================
   * ✅ CREATE EVENT
   * =========================================================
   *
   */
  const handleCreateEvent = async (eventData) => {
    if (!selectedSchoolyear?.id) return;

    await eventService.createEvent({
      ...eventData,
      schoolyearId: selectedSchoolyear.id,
      role: eventData.role,
    });
  };

  const updateEvent = async (event) => {
    return await eventService.updateEvent(event.id, event);
  };

  const handleSaveEvent = async (data, editingEvent, close) => {
    try {
      if (editingEvent) {
        await updateEvent(data);
      } else {
        await handleCreateEvent(data);
      }

      close(); // ✅ only closes on success
    } catch (err) {
      console.log("Event konnte nicht gespeichert werden", err);
    }
  };

  const handleArchiveEvent = async (eventId) => {
    await eventService.archiveEvent(eventId);

    // await loadSommerData(selectedEvent.id);
  };

  const handleDeleteEvent = async (eventId) => {
    await eventService.deleteEvent(eventId);

    // ✅ reload everything
    await loadSommerData(selectedSchoolyear.id);

    // ✅ reset selected event if needed
    setSelectedEvent(null);
  };

  /**
   * =========================================================
   * ✅ ADD SHIFT (OPTIMISTIC ✅)
   * =========================================================
   */
  const addShift = async (shift) => {
    if (!selectedEvent?.id) return;

    const tempId = "temp-" + Date.now();

    const fakeShift = {
      ...shift,
      id: tempId,
      eventId: selectedEvent.id,
      jobs: [],
    };

    // ✅ optimistic
    setShifts((prev) => [...prev, fakeShift]);

    try {
      const realShift = await eventService.createShift({
        ...shift,
        eventId: selectedEvent.id,
      });

      // ✅ replace temp
      setShifts((prev) => prev.map((s) => (s.id === tempId ? realShift : s)));

      return realShift;
    } catch (err) {
      console.error("Create shift failed → rollback", err);

      // ❌ rollback
      setShifts((prev) => prev.filter((s) => s.id !== tempId));
    }
  };

  /**
   * =========================================================
   * ✅ DELETE SHIFT (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleDeleteShift = async (shiftId) => {
    const old = shifts;

    // ✅ optimistic
    setShifts((prev) => prev.filter((s) => s.id !== shiftId));

    try {
      await eventService.deleteShift(shiftId);
    } catch (err) {
      console.error("Delete shift failed → rollback", err);
      setShifts(old); // rollback
    }
  };

  /**
   * =========================================================
   * ✅ UPDATE SHIFT
   * =========================================================
   * (keep non-optimistic → complex structure)
   */
  const updateShift = async (shift) => {
    return await eventService.updateShift(shift.id, shift);
  };

  const handleSaveShift = async (shift, editingShift) => {
    return editingShift ? updateShift(shift) : addShift(shift);
  };

  /**
   * =========================================================
   * ✅ JOBS (safe → keep API-first)
   * =========================================================
   */
  const addJob = async (job) => {
    const res = await eventService.createJob(job);

    // ✅ reload full structure
    await loadSommerData(selectedEvent.id);

    return res;
  };

  const updateJob = async (job) => {
    await eventService.updateJob(job.id, job);

    await loadSommerData(selectedEvent.id);
  };

  const handleSaveJob = async (job, editingJob) => {
    return editingJob ? updateJob(job) : addJob(job);
  };

  const handleDeleteJob = async (jobId) => {
    await eventService.deleteJob(jobId);

    await loadSommerData(selectedEvent.id);
  };

  /**
   * =========================================================
   * ✅ LOCK (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleToggleLock = async ({
    unlockTime,
    lockForStudents,
    lockForTeachers,
  }) => {
    if (!selectedEvent?.id) return;

    const prevState = {
      unlockTime: selectedEvent.unlockTime,
      lockForStudents: selectedEvent.lockForStudents,
      lockForTeachers: selectedEvent.lockForTeachers,
    };

    // ✅ optimistic update
    setSelectedEvent((prev) => ({
      ...prev,
      unlockTime,
      lockForStudents,
      lockForTeachers,
    }));

    try {
      await eventService.updateLock(selectedEvent.id, {
        unlockTime,
        lockForStudents,
        lockForTeachers,
      });
    } catch (err) {
      console.error("Lock update failed → rollback", err);

      // ❌ rollback
      setSelectedEvent((prev) => ({
        ...prev,
        ...prevState,
      }));
    }
  };

  /**
   * =========================================================
   * ✅ UNLOCK NOW (OPTIMISTIC ✅)
   * =========================================================
   */
  const handleUnlockNow = async () => {
    if (!selectedEvent?.id) return;

    const prevState = {
      unlockTime: selectedEvent.unlockTime,
      lockForStudents: selectedEvent.lockForStudents,
      lockForTeachers: selectedEvent.lockForTeachers,
    };

    // ✅ optimistic update → clear everything
    setSelectedEvent((prev) => ({
      ...prev,
      unlockTime: null,
      lockForStudents: false,
      lockForTeachers: false,
    }));

    try {
      await eventService.updateLock(selectedEvent.id, {
        unlockTime: null,
        lockForStudents: false,
        lockForTeachers: false,
      });
    } catch (err) {
      console.error("Unlock failed → rollback", err);

      setSelectedEvent((prev) => ({
        ...prev,
        ...prevState,
      }));
    }
  };

  /**
   * =========================================================
   * ✅ RETURN API
   * =========================================================
   */
  return {
    handleReserve,
    handleCreateEvent,
    handleSaveEvent,
    handleArchiveEvent,
    handleDeleteEvent,
    handleSaveJob,
    handleDeleteJob,
    handleSaveShift,
    handleDeleteShift,
    handleToggleLock,
    handleUnlockNow,
  };
}
